// EDEN · the Lead's class manager — every class, its teacher, its periods per week, and "add a class
// for a teacher" (2026-09-30, owner-approved). Shown in Setup › Classes and on /classes for the Lead.
//
// Three separate saves, each its own API, so no edit can ride along with another:
//   · teacher  → PATCH /api/admin/classes/:id  { teacher_id }   (Lead-only; saves on change)
//   · periods  → PATCH /api/classes/:id        { periods_per_week } (Lead-only; the old form, kept)
//   · new class→ POST  /api/admin/classes      { name, level, teacher_id, periods_per_week }
// Renaming, changing the level and deleting a class live on the class page (2026-10-07); each row links there.

import { api } from "../api";
import { esc } from "../dom";
import { icon } from "../icons";
import { splitPeriods } from "../materials";
import { byGrade, type ClassRow } from "../lesson-data";
import { toast } from "../ui";

interface Person { id: string; name: string; auth_role: "teacher" | "lead"; active: boolean }
interface Cls extends ClassRow { teacher_id: string; periods_per_week: number }

export function splitLabel(periods: number): string {
  const split = splitPeriods(periods);
  // The same words as the Week page's period picker (Cambridge = the exam thread, reading = fluency).
  return `Coursebook ${split.coursebook} · Cambridge ${split.thread} · Reading ${split.fluency}`;
}

function teacherOptions(people: Person[], selected: string | null): string {
  const active = people.filter((p) => p.active);
  const missing = selected && !active.some((p) => p.id === selected) ? `<option value="${esc(selected)}" selected>(inactive account)</option>` : "";
  return missing + active
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((p) => `<option value="${esc(p.id)}"${p.id === selected ? " selected" : ""}>${esc(p.name)}${p.auth_role === "lead" ? " (Lead)" : ""}</option>`)
    .join("");
}

function rowHtml(c: Cls, people: Person[]): string {
  return `<tr data-class-id="${esc(c.id)}">
    <td><a data-link href="/classes/${esc(c.id)}"><b>${esc(c.name)}</b></a><br/><a class="small" data-link data-gradebook-link href="/classes/${encodeURIComponent(c.id)}/gradebook">Private marks & completeness</a><br/><a class="small" data-link data-class-settings-link href="/classes/${encodeURIComponent(c.id)}#class-settings">Rename or delete</a></td>
    <td>${esc(c.level ?? "")}</td>
    <td><select data-act="assign" aria-label="Teacher for ${esc(c.name)}">${teacherOptions(people, c.teacher_id)}</select>
      <p class="hint assign-status" role="status" aria-live="polite"></p></td>
    <td>
      <form class="class-periods">
        <div class="row-actions">
          <input id="periods-${esc(c.id)}" name="periods" type="number" min="5" max="40" step="1" required value="${c.periods_per_week}" aria-label="Periods per week for ${esc(c.name)}" />
          <button class="btn sm secondary" type="submit">Save</button>
        </div>
        <p class="hint period-split">${splitLabel(c.periods_per_week)}</p>
        <p class="hint period-status" role="status" aria-live="polite"></p>
      </form>
    </td>
  </tr>`;
}

function noClassNote(withoutClass: Person[]): string {
  return withoutClass.length
    ? `<p class="note">${icon("info")}<span>No class yet: ${withoutClass.map((u) => esc(u.name)).join(", ")}. Add a class for them below — it appears on their Home straight away.</span></p>`
    : "";
}

/** After a move, only the "no class yet" note can go stale — redraw just that. */
async function refreshNoClassNote(host: HTMLElement): Promise<void> {
  try {
    const [{ classes }, { users }] = await Promise.all([api<{ classes: Cls[] }>("/api/classes"), api<{ users: Person[] }>("/api/admin/users")]);
    const box = host.querySelector<HTMLElement>(".cm-noclass");
    if (box) box.innerHTML = noClassNote(users.filter((u) => u.active && u.auth_role === "teacher" && !classes.some((c) => c.teacher_id === u.id)));
  } catch { /* the note is a nicety */ }
}

/** Render the manager into `host` and wire it. Adding a class redraws it; a move or a timetable save
 *  changes only its own row. */
export async function mountClassManager(host: HTMLElement, opts: { addOpen?: boolean } = {}): Promise<void> {
  // A re-render after moving a class or saving a timetable keeps the "Add a class" fold as it was,
  // so the page does not jump; after adding a class it closes (the job is done).
  const addOpen = opts.addOpen ?? !!host.querySelector<HTMLDetailsElement>("#add-class-fold")?.open;
  const [{ classes }, { users }] = await Promise.all([
    api<{ classes: Cls[] }>("/api/classes"),
    api<{ users: Person[] }>("/api/admin/users"),
  ]);
  const sorted = [...classes].sort(byGrade);
  const withoutClass = users.filter((u) => u.active && u.auth_role === "teacher" && !classes.some((c) => c.teacher_id === u.id));

  host.innerHTML = `
    <div class="cm-noclass">${noClassNote(withoutClass)}</div>
    ${sorted.length ? `<p class="hint">Pick a new teacher and the class moves at once — it shows on their Home straight away. A plan already written keeps its writer: only that teacher and you can open it.</p>
    <div class="table-wrap"><table class="tbl tbl-classes">
      <thead><tr><th>Class</th><th>Level</th><th>Teacher</th><th>Periods per week</th></tr></thead>
      <tbody>${sorted.map((c) => rowHtml(c, users)).join("")}</tbody>
    </table></div>` : `<p class="muted">No classes yet.</p>`}
    <details class="card fold" id="add-class-fold"${!sorted.length || addOpen ? " open" : ""}>
      <summary>${icon("plus")}<span>Add a class for a teacher</span>${icon("down", "ico-sm fold-caret")}</summary>
      <form id="add-class" class="fold-body form-grid">
        <label>Class name<input name="name" required maxlength="40" placeholder="G5A" /></label>
        <label>Level (optional)<input name="level" maxlength="40" placeholder="Movers" /></label>
        <label>Teacher<select name="teacher_id" required><option value="">Choose a teacher</option>${teacherOptions(users, null)}</select></label>
        <label>Periods per week<input name="periods_per_week" type="number" min="5" max="40" value="12" required /></label>
        <p class="hint span-2">Name the class with its grade first (for example “G5A”) so EDEN can find its year plan.</p>
        <div class="form-actions span-2"><button class="btn" type="submit">${icon("plus")}<span>Add class</span></button></div>
      </form>
    </details>`;

  // Change the teacher: saves as soon as a new name is picked. Only this row changes — nothing else
  // on the page is redrawn, so a half-typed class or timetable elsewhere is never lost.
  (Array.from(host.querySelectorAll('select[data-act="assign"]')) as unknown as HTMLSelectElement[]).forEach((sel) => {
    let before = sel.value;
    const status = sel.closest("td")?.querySelector<HTMLElement>(".assign-status") ?? null;
    sel.addEventListener("change", async () => {
      const row = sel.closest<HTMLElement>("[data-class-id]")!;
      const who = sel.selectedOptions[0]?.textContent ?? "the new teacher";
      sel.disabled = true;
      if (status) status.textContent = "Saving…";
      try {
        await api(`/api/admin/classes/${encodeURIComponent(row.dataset.classId!)}`, { method: "PATCH", body: JSON.stringify({ teacher_id: sel.value }) });
        before = sel.value;
        if (status) status.textContent = `Saved — ${who} teaches this class now.`;
        toast(`Class moved to ${who}.`);
        void refreshNoClassNote(host);
      } catch (ex) {
        sel.value = before;
        if (status) status.textContent = "";
        toast((ex as Error).message || "Couldn't move the class.");
      } finally {
        sel.disabled = false;
      }
    });
  });

  // Periods per week (the existing Lead timetable form, unchanged in behaviour).
  host.querySelectorAll<HTMLFormElement>(".class-periods").forEach((form) => {
    const input = form.elements.namedItem("periods") as HTMLInputElement;
    const hint = form.querySelector<HTMLElement>(".period-split")!;
    const status = form.querySelector<HTMLElement>(".period-status")!;
    input.addEventListener("input", () => {
      status.textContent = "";
      hint.textContent = input.checkValidity() ? splitLabel(input.valueAsNumber) : "Use a whole number from 5 to 40.";
    });
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;
      const button = form.querySelector<HTMLButtonElement>("button")!;
      button.disabled = true;
      status.textContent = "Saving…";
      const classId = form.closest<HTMLElement>("[data-class-id]")!.dataset.classId!;
      try {
        const response = await api<{ class: Cls }>(`/api/classes/${encodeURIComponent(classId)}`, {
          method: "PATCH", body: JSON.stringify({ periods_per_week: input.valueAsNumber }),
        });
        input.value = String(response.class.periods_per_week);
        hint.textContent = splitLabel(response.class.periods_per_week);
        status.textContent = "Saved. The Week card and new plans use this timetable.";
      } catch (error) {
        status.textContent = (error as Error).message || "Couldn't save the timetable. Please try again.";
      } finally {
        button.disabled = false;
      }
    });
  });

  // Add a class for a teacher.
  host.querySelector<HTMLFormElement>("#add-class")?.addEventListener("submit", async (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const fd = new FormData(form);
    const body = {
      name: String(fd.get("name") ?? "").trim(),
      level: String(fd.get("level") ?? "").trim() || null,
      teacher_id: String(fd.get("teacher_id") ?? ""),
      periods_per_week: Number(fd.get("periods_per_week")) || 12,
    };
    if (!body.name || !body.teacher_id) return;
    const btn = form.querySelector<HTMLButtonElement>('button[type="submit"]')!;
    btn.disabled = true;
    try {
      await api("/api/admin/classes", { method: "POST", body: JSON.stringify(body) });
      toast(`Added ${body.name}.`);
      await mountClassManager(host, { addOpen: false });
    } catch (ex) {
      btn.disabled = false;
      toast((ex as Error).message || "Couldn't add the class.");
    }
  });
}
